import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

// A small "?" after a column header that explains the column. The tooltip opens
// on hover and on keyboard focus, closes on Esc, and is drawn on <body> so a
// sticky header or a scroll box can't clip it. Clicking it never sorts the table.
// Styles are inline because the admin pages load React packs without their CSS.
// The ActiveAdmin version is AdminHelper#help_label, styled in active_admin.scss.

const WIDTH = 280;

const iconStyle = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: "14px", height: "14px", marginLeft: "4px", padding: 0, verticalAlign: "-2px",
  border: "1px solid currentColor", borderRadius: "50%", background: "none", color: "inherit",
  fontSize: "9px", fontWeight: 700, lineHeight: 1, letterSpacing: 0, textTransform: "none",
  cursor: "help", opacity: 0.7,
};
const tipStyle = {
  position: "fixed", zIndex: 10000, width: "max-content", maxWidth: `${WIDTH}px`,
  background: "#212529", color: "#fff", borderRadius: "6px", padding: "8px 10px",
  fontSize: "12px", fontWeight: 400, lineHeight: 1.4, textAlign: "left",
  textTransform: "none", letterSpacing: 0, whiteSpace: "normal",
  boxShadow: "0 4px 12px rgba(0,0,0,0.2)", pointerEvents: "none",
};

let nextId = 0;

export const HeaderHelp = ({ label, text }) => {
  const [id] = useState(() => `header-help-${(nextId += 1)}`);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  const open = () => {
    const r = ref.current.getBoundingClientRect();
    const left = Math.min(Math.max(8, r.left + r.width / 2 - WIDTH / 2), window.innerWidth - WIDTH - 8);
    setPos({ left, top: r.bottom + 6 });
  };
  const close = () => setPos(null);

  useEffect(() => {
    if (!pos) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [pos]);

  return (
    <>
      <button
        ref={ref} type="button" style={iconStyle} aria-label={`About ${label}`} aria-describedby={id}
        onMouseEnter={open} onMouseLeave={close} onFocus={open} onBlur={close}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pos) close(); else open(); }}
      >
        ?
      </button>
      {ReactDOM.createPortal(
        <span id={id} role="tooltip" style={pos ? { ...tipStyle, ...pos } : { display: "none" }}>{text}</span>,
        document.body,
      )}
    </>
  );
};

// A header title followed by its "?" when there is help text for it.
export const HelpTitle = ({ title, help }) => (
  <>
    {title}
    {help && <HeaderHelp label={title} text={help} />}
  </>
);

export default HeaderHelp;
